import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Grid, Box, Flex, Text, Image, Divider, Icon, ButtonBase, Stack } from '@tonic-ui/react';
import Carousel from 'react-elastic-carousel';
import { useTranslation, Trans } from 'react-i18next';
import ReadmoreLink from '../learn-more-link/learn-more';
import zachEvansPhoto from '../../../assets/landing/img/Zach-Evans-desktop.jpg';
import xsolisLogo from '../../../assets/landing/img/XSOLIS_logo.png';
import jasonCraditPhoto from '../../../assets/landing/img/Jason-Cradit-desktop.jpg';
import eighteen98logo from '../../../assets/landing/img/1898_logo.png';
import flowServePhoto from '../../../assets/landing/img/flowserve_photo.jpg';
import flowServeLogo from '../../../assets/landing/img/Flowserve_logo.png';

interface Customer {
  name: string;
  role: string;
  storyLink: string;
}

interface Company {
  name: string;
  logoPath: string;
}

interface Testimony {
  customer: Customer;
  imgSrc: string;
  company: Company;
  comment: string;
}

type TestimonyProps = Testimony;

const TestimonyTile = ({ comment, customer, imgSrc }: TestimonyProps) => {
  const { t } = useTranslation();

  return (
    <Box align="center" justify="center" userSelect="none" cursor="grab" overflowX="hidden">
      <Flex direction="column">
        <Text fontSize="28px" lineHeight="42px" textAlign="center" marginBottom="40px" width="100%">
          {comment}
        </Text>
        <Grid
          gridTemplateColumns={{ sm: '1fr', md: '1fr 1fr' }}
          columnGap="54px"
          rowGap="54px"
          marginBottom="40px"
        >
          <Box maxWidth="100%" height="280px">
            <Image
              src={imgSrc}
              // Disable default browser image dragging in carousel
              draggable={false}
              alt={`Contextual image for customer (${customer.name})`}
              overflow="hidden"
              maxHeight="280px"
              objectFit="contain"
              borderRadius="2px"
              width="100%"
            />
          </Box>
          <Flex direction="column" fontSize="14px" lineHeight="21px">
            <Text>{customer.name}</Text>
            <Text>{customer.role}</Text>
            <Divider width="74px" height="3px" bg="black" marginTop="3x" marginBottom="56px" />
            <Box>
              <ReadmoreLink href={customer.storyLink}>
                {t('publicLandingPage.customers.readStory')}
              </ReadmoreLink>
            </Box>
          </Flex>
        </Grid>
      </Flex>
    </Box>
  );
};

const addLeadingZero = (page: number) => {
  return page.toString().padStart(2, '0');
};

const BottomPagination = ({
  pages,
  activePage,
  companies,
  onClick,
}: {
  pages: number[];
  activePage: number;
  companies: Company[];
  onClick: (indicatorId: string) => void;
}) => {
  const pageCount = pages.length;
  const canGoBack = activePage > 0;
  const canGoNext = activePage < pageCount - 1;
  const totalPages = `${addLeadingZero(pageCount)}`;

  const buttonStyles = {
    padding: '3x',
    _disabled: { opacity: '0.35' },
    _hover: {
      borderRadius: '4px',
      backgroundColor: '#adadad',
      color: 'rgba(255, 255, 255, 0.92)',
    },
    _focus: {
      boxShadow: 'inset 0 0 0 0.0625rem #4D4D4F, inset 0 0 0 0.125rem rgba(255, 255, 255, 1.0)',
      // border: '.0625rem solid',
      // borderColor: '#4D4D4F',
      // borderRadius: '.1875rem',
      outlineColor: '#4D4D4F',
    },
  };

  return (
    <Box>
      <Flex direction="row" justify="center" columnGap="50px">
        {companies.map(({ name, logoPath }, i) => (
          <Flex width="100px" justifyContent="center" key={name}>
            <Image
              src={logoPath}
              alt={`Company logo of "${name}"`}
              height="38px"
              userSelect="none"
              {...(i !== activePage && {
                filter: 'grayscale(1)',
                opacity: 0.5,
              })}
            />
          </Flex>
        ))}
      </Flex>
      <Flex justifyContent="center" alignItems="center" columnGap="2x" paddingTop="40px">
        <ButtonBase
          disabled={!canGoBack}
          onClick={() => onClick(`${activePage - 1}`)}
          {...buttonStyles}
        >
          <Icon icon="chevron-left" />
        </ButtonBase>
        <div>{`${addLeadingZero(activePage + 1)} / ${totalPages}`}</div>
        <ButtonBase
          disabled={!canGoNext}
          onClick={() => onClick(`${activePage + 1}`)}
          {...buttonStyles}
        >
          <Icon icon="chevron-right" />
        </ButtonBase>
      </Flex>
    </Box>
  );
};

const CustomersCarousel = () => {
  const { t } = useTranslation();

  const testimonies: Testimony[] = useMemo(
    () => [
      {
        comment: t('publicLandingPage.customers.johnBreen.comment'),
        imgSrc: flowServePhoto,
        customer: {
          name: t('publicLandingPage.customers.johnBreen.name'),
          role: t('publicLandingPage.customers.johnBreen.role'),
          storyLink: t('publicLandingPage.customers.johnBreen.storyLink'),
        },
        company: { name: 'flowserve', logoPath: flowServeLogo },
      },
      {
        comment: t('publicLandingPage.customers.jasonCradit.comment'),
        imgSrc: jasonCraditPhoto,
        customer: {
          name: t('publicLandingPage.customers.jasonCradit.name'),
          role: t('publicLandingPage.customers.jasonCradit.role'),
          storyLink: t('publicLandingPage.customers.jasonCradit.storyLink'),
        },
        company: { name: '1898', logoPath: eighteen98logo },
      },
      {
        comment: t('publicLandingPage.customers.zachEvans.comment'),
        imgSrc: zachEvansPhoto,
        customer: {
          name: t('publicLandingPage.customers.zachEvans.name'),
          role: t('publicLandingPage.customers.zachEvans.role'),
          storyLink: t('publicLandingPage.customers.zachEvans.storyLink'),
        },
        company: { name: 'xsolis', logoPath: xsolisLogo },
      },
    ],
    [t]
  );

  const companies = testimonies.map(({ company }) => company);

  return (
    <Flex justifyContent="center" marginX={{ md: '90px', lg: '120px' }}>
      <Stack
        position="relative"
        fontFamily="interstate"
        alignItems="center"
        color="#4D4D4F"
        width="100%"
        maxWidth="1200px"
        boxShadow="0px 0px 20px rgba(0, 0, 0, 0.25)"
        paddingTop="46px"
        paddingBottom="83px"
        paddingX={{ sm: '10%', md: '120px' }}
        borderRadius="16px"
      >
        <Text color="red:70" fontWeight="700" fontSize="14px" marginBottom="4x">
          <Trans i18nKey="publicLandingPage.customers.testimonials" />
        </Text>
        <Text fontSize="48px" lineHeight="57px" marginBottom="10x" textAlign="center">
          <Trans i18nKey="publicLandingPage.customers.heading" />
        </Text>
        <Carousel
          isRTL={false}
          showArrows={false}
          renderPagination={(props) => <BottomPagination companies={companies} {...props} />}
        >
          {testimonies.map((testimony) => (
            <TestimonyTile {...testimony} key={testimony.customer.name} />
          ))}
        </Carousel>
      </Stack>
    </Flex>
  );
};

CustomersCarousel.displayName = 'CustomersCarousel';

export default CustomersCarousel;
